import React, { useState, useMemo } from 'react';
import { Scale } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, GenderField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBMI, getBMICategory, getHealthyWeightRange } from '../../lib/calculatorUtils';

const BMICalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!h || !w || h < 50 || w < 20) return null;
    const bmi = calculateBMI(w, h);
    return {
      bmi,
      category: getBMICategory(bmi),
      range: getHealthyWeightRange(h)
    };
  }, [heightCm, weightKg]);

  const inputs = (
    <div className="space-y-5" data-testid="bmi-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Enter Your Details</h2>
      <p className="text-slate-400 text-sm mb-5">Real-time calculation as you type.</p>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <div className="grid grid-cols-2 gap-3">
        <NumberField id="bmi-age" label="Age" value={age} onChange={setAge} placeholder="e.g. 28" suffix="yrs" min={2} max={120} testId="bmi-age-input" />
        <GenderField value={gender} onChange={setGender} testIdPrefix="bmi-gender" />
      </div>
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Your BMI" value={result.bmi} accent="from-blue-500 to-cyan-500" category={result.category} testId="bmi-result-value" />
      <div className="mt-8 sm:mt-10 space-y-1">
        <StatRow label="Category" value={result.category.label} color={result.category.color} testId="bmi-category" />
        <StatRow label="Healthy weight range" value={`${result.range.min} – ${result.range.max}`} unit="kg" testId="bmi-healthy-range" />
        <StatRow label="Underweight" value="< 18.5" />
        <StatRow label="Normal" value="18.5 – 24.9" />
        <StatRow label="Overweight" value="25 – 29.9" />
        <StatRow label="Obese" value="≥ 30" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        BMI is a screening tool and not a diagnosis. It does not distinguish between muscle and fat mass. Always consult a healthcare professional for personalized guidance.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your height and weight to see your BMI score, category, and healthy weight range." />
  );

  return (
    <CalculatorLayout
      slug="bmi"
      title="BMI Calculator"
      subtitle="Calculate your Body Mass Index and check whether your weight falls within a healthy range for your height."
      metaDescription="Free online BMI Calculator from TCUBE FITNESS to calculate your Body Mass Index in metric or imperial units and see your healthy weight range."
      icon={Scale}
      accentGradient="from-blue-500 to-cyan-500"
      accentColor="text-blue-400"
      badgeBg="bg-blue-500/10"
      badgeBorder="border-blue-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">Formula:</strong> BMI = weight (kg) ÷ [height (m)]²</p>
          <p>BMI categories follow the World Health Organization (WHO) classification: Underweight (&lt;18.5), Normal (18.5–24.9), Overweight (25–29.9), Obese (≥30).</p>
        </>
      }
    />
  );
};

export default BMICalculatorPage;
